import {
  APIComponentAttributes,
  APIViewpointAttributes,
  ArdoqId,
  ComponentSelection,
  isTraversalLoadedState,
  isCreatedItemsLoadedState,
  hasStartSet,
  LoadedState,
  LoadedStateParams,
  QueryBuilderQuery,
  ViewIds,
} from '@ardoq/api-types';
import { viewpointSwitcherCommands } from 'canvas/sidebarPanel/sidebars/viewpointSwitcher/viewpointSwitcherCommands';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import type { ComponentRepresentationData } from '@ardoq/basic-components';
import {
  SidebarPanelContentIds,
  SidebarPanelState,
} from 'canvas/sidebarPanel/types';
import { CollectionStream, StreamCollection } from 'streams/utils/streamUtils';
import { getRepresentationDataWithColorVariantsForComponent } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { isComponent } from '@ardoq/api-types';
import { isDefaultWildcardTraversal } from 'traverseOneLevel/defaultWildcardViewpoint';
import { getDefaultOverviewName } from 'viewpoints/defaultOverviewName';
import type { WorkspaceAndModelLookup } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import { getExcludedComponentIds } from 'loadedState/excludeComponent/excludeComponentOperations';
import { ContextSelectorTrackingCallbacks } from '@ardoq/component-hierarchies';
import { uniq } from 'lodash';
import { isSameSet, isTruthy, pluralizeWithCount } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { IconName } from '@ardoq/icons';
import { ButtonSize } from '@ardoq/button';
import { CurrentToolbarContext } from 'canvas/contextToolBar/types';
import type { LoadedStatesState } from 'loadedState/loadedStateOperations';
import { getTypeQuery } from 'canvas/utils/getTypeQuery';
import { toggleSidebarPanel } from 'canvas/sidebarPanel/actions';
import { trackEventV2 } from 'tracking/tracking';
import {
  getDisabledButtonPopoverText,
  ToolbarButtonId,
  ViewpointToolBarDisabledReason,
} from 'canvas/contextToolBar/viewpointToolBar/getDisabledText';
import { getStartTypeFromLoadedState } from '../loadedStateHistory/rebuildStateFromPoppedHistoryUtils';
import { clearGraphSelection } from '@ardoq/component-selection';
import { anyType } from '@ardoq/global-consts';
import {
  setStartQueryForLoadedState,
  setStartSetForLoadedState,
} from 'loadedState/setStartContextForLoadedState';
import { getRepresentationDataWithColorVariants } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  isCurrentViewpointId,
  isDefaultOverviewId,
} from 'viewpoints/defaultOverview';
import { getDefaultViewpoint } from 'viewpoints/getDefaultViewpoint';
import type { Locale } from '@ardoq/locale';
import { FeatureContext, featureOperations, Features } from '@ardoq/features';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { toInventoryLocation } from 'inventory/inventoryLocation$';
import { InventoryModule } from 'inventory/consts';

export type ViewpointToolBarButton = {
  onClick: () => void;
  text: string;
  isActive: boolean;
  iconName: IconName;
  buttonSize: ButtonSize;
  isDisabled: boolean;
  disabledPopoverText?: string;
};

export type PriorityViewpointItem = {
  name: string;
  viewName: ViewIds;
  description: string | undefined;
  isCurrent: boolean;
  isDefaultOverview: boolean;
  onSelect: VoidFunction;
};

export type DisabledViewpointToolBar = {
  status: 'disabled';
  datasetCount: number;
  /** Carried here too: see the note on `ShowExcludedButton` for why every branch needs it. */
  excludedComponentCount: number;
  disabledReason: ViewpointToolBarDisabledReason;
  isDiscover: boolean;
  datasetsButton: ViewpointToolBarButton;
  viewpointButton: ViewpointToolBarButton;
  openInInventoryButton?: ViewpointToolBarButton;
  contextDropdownDisabledText: string;
  i18nLocale: I18nLocale;
};

export type ContextDropdown = {
  onContextDropdownComponentSelected: (components: string[]) => void;
  onSelectAll: () => void;
  query: QueryBuilderQuery;
  currentContext: CurrentToolbarContext;
  startSetComponentIds: string[];
  tracking: ContextSelectorTrackingCallbacks;
  locale: Locale;
};

export type ViewpointToolBar = {
  status: 'enabled';
  currentContext: CurrentToolbarContext;
  currentViewpointId: string | null;
  currentOpenSidebar: SidebarPanelContentIds | null;
  datasetCount: number;
  detailsButton: ViewpointToolBarButton;
  viewpointButton: ViewpointToolBarButton;
  priorityViewpointItems: PriorityViewpointItem[];
  seeAllViewpointsLabel: string;
  defaultOverviewLabel: string;
  contextDropdown: ContextDropdown;
  datasetsButton: ViewpointToolBarButton;
  // Only present when the INVENTORY_ALPHA feature flag is enabled.
  openInInventoryButton?: ViewpointToolBarButton;
  isDiscover: boolean;
  isWildcardTraversal: boolean;
  clearSelection: () => void;
  /**
   * How many components the datasets are excluding. 0 hides the control entirely — it is the only
   * standing sign that anything is hidden, since the traversal filter behind an exclusion is not
   * something the viewpoint builder can render.
   */
  excludedComponentCount: number;
  i18nLocale: I18nLocale;
};

export type ViewpointToolBarState = ViewpointToolBar | DisabledViewpointToolBar;

export const isEnabledViewpointToolBar = (
  state: ViewpointToolBarState
): state is ViewpointToolBar => state.status === 'enabled';

const getComponentTypeName = (
  componentId: ArdoqId,
  components: StreamCollection<APIComponentAttributes>,
  workspaceAndModel: WorkspaceAndModelLookup
): string | null => {
  const component = components.byId[componentId];
  if (!component) return null;
  return (
    scopeDataOperations.getComponentTypeFromWorkspaceLookup(
      workspaceAndModel,
      component
    )?.name ?? null
  );
};

const isComponentId = (
  componentId: string,
  components: StreamCollection<APIComponentAttributes>
): boolean => Boolean(components.byId[componentId]);

const getStartTypeFromIds = (
  ids: string[],
  components: StreamCollection<APIComponentAttributes>,
  workspaceAndModel: WorkspaceAndModelLookup
): string => {
  const types = uniq(
    ids.map(id => getComponentTypeName(id, components, workspaceAndModel))
  ).filter(isTruthy);
  if (types.length === 1) return types[0];
  return anyType;
};

const getSelectionStatus = (
  i18nLocale: I18nLocale,
  selection: string[],
  firstComponentName: string | null,
  components: StreamCollection<APIComponentAttributes>,
  workspaceAndModel: WorkspaceAndModelLookup
) => {
  if (!selection.length) {
    return {
      label: t(i18nLocale, 'canvas:contextToolbarNoSelection', 'No selection'),
      isMultipleTypesSelected: false,
    };
  }
  if (selection.length === 1) {
    return {
      label:
        firstComponentName ??
        t(
          i18nLocale,
          'canvas:contextToolbarUnknownComponent',
          'Unknown component'
        ),
      isMultipleTypesSelected: false,
    };
  }
  const types = uniq(
    selection.map(id => getComponentTypeName(id, components, workspaceAndModel))
  ).filter(isTruthy);
  if (types.length === 1) {
    return {
      label: pluralizeWithCount(types[0], selection.length),
      isMultipleTypesSelected: false,
    };
  }
  return {
    label: t(
      i18nLocale,
      'canvas:contextToolbarComponentsOfTypes',
      { count: selection.length, typeCount: types.length },
      `${selection.length} components of ${types.length} types`
    ),
    isMultipleTypesSelected: true,
  };
};

const getIsWildcardTraversal = (loadedState: LoadedState | null) => {
  if (!loadedState || !isTraversalLoadedState(loadedState)) return false;
  const {
    data: { paths },
  } = loadedState;
  return isDefaultWildcardTraversal(paths);
};

const getComponentSelection = (
  loadedState: LoadedState,
  graphSelection: string[]
): ComponentSelection => {
  if (graphSelection.length > 0) {
    return {
      startSet: graphSelection,
    };
  }
  if (isCreatedItemsLoadedState(loadedState)) {
    return {
      startSet: loadedState.data.componentIds,
    };
  }

  return loadedState.data.componentSelection;
};

const getDetailButton = (
  i18nLocale: I18nLocale,
  currentSideBar: SidebarPanelContentIds | null,
  dataSetCount: number,
  currentSelection: string[],
  isDiscover: boolean
): ViewpointToolBarButton => {
  const hasInvalidSelection =
    (dataSetCount > 1 && currentSelection.length === 0) ||
    currentSelection.length > 1;

  // should always be enabled in discover
  const isDisabled = !isDiscover && hasInvalidSelection;

  return {
    onClick: () => toggleSidebar(SidebarPanelContentIds.DETAILS),
    text: t(i18nLocale, 'canvas:contextToolbarDetailsText', 'Details'),
    isActive: currentSideBar === SidebarPanelContentIds.DETAILS,
    iconName: IconName.DESCRIPTION,
    buttonSize: ButtonSize.SMALL,
    isDisabled,
    disabledPopoverText: isDisabled
      ? t(
          i18nLocale,
          'canvas:contextToolbarSelectSingleComponent',
          'Select a single component to view details'
        )
      : undefined,
  };
};

type ViewpointButtonArgs = {
  i18nLocale: I18nLocale;
  currentViewpointId: string | null;
  viewpoints: Record<ArdoqId, APIViewpointAttributes>;
  startType: string | null;
  isWildcardTraversal: boolean;
  isMultipleTypesSelected: boolean;
  graphSelection: string[];
  isUserSelectionMatchingStartSet: boolean;
  currentSidebar: SidebarPanelContentIds | null;
};

const toggleSidebar = (contentId: SidebarPanelContentIds) => {
  dispatchAction(toggleSidebarPanel(contentId));
  trackEventV2({
    event: 'Viewpoint Toolbar0 Toggled sidebar panel',
    payload: { panel: contentId },
  });
};

const getViewpointButtonText = ({
  i18nLocale,
  currentViewpointId,
  viewpoints,
  startType,
  isWildcardTraversal,
  graphSelection,
  isUserSelectionMatchingStartSet,
}: Omit<ViewpointButtonArgs, 'currentSidebar'>): string => {
  const hasActiveViewpoint =
    currentViewpointId && viewpoints[currentViewpointId];

  const hasNoSelection = graphSelection.length === 0;
  const isSelectionStartSetOrEmpty =
    isUserSelectionMatchingStartSet || hasNoSelection;

  if (hasActiveViewpoint && isSelectionStartSetOrEmpty) {
    return viewpoints[currentViewpointId].name;
  }

  if (isWildcardTraversal && isSelectionStartSetOrEmpty && startType) {
    return getDefaultOverviewName(startType, i18nLocale);
  }

  return t(i18nLocale, 'canvas:contextToolbarViewpointsText', 'Viewpoints');
};

const getViewpointButton = ({
  i18nLocale,
  currentViewpointId,
  viewpoints,
  startType,
  isWildcardTraversal,
  isMultipleTypesSelected,
  graphSelection,
  isUserSelectionMatchingStartSet,
  currentSidebar,
}: ViewpointButtonArgs): ViewpointToolBarButton => {
  return {
    onClick: () => toggleSidebar(SidebarPanelContentIds.VIEWPOINT_SWITCHER),
    text: getViewpointButtonText({
      i18nLocale,
      currentViewpointId,
      viewpoints,
      startType,
      isWildcardTraversal,
      isMultipleTypesSelected,
      graphSelection,
      isUserSelectionMatchingStartSet,
    }),
    isActive: currentSidebar === SidebarPanelContentIds.VIEWPOINT_SWITCHER,
    iconName: IconName.VIEWPOINT,
    buttonSize: ButtonSize.SMALL,
    isDisabled: isMultipleTypesSelected,
  };
};

const getContextDropdown = (
  i18nLocale: I18nLocale,
  locale: Locale,
  loadedState: LoadedState,
  currentContext: CurrentToolbarContext,
  components: StreamCollection<APIComponentAttributes>
): ContextDropdown => {
  const startSetComponentIds = getStartSetIds(loadedState);
  const query = getTypeQuery(getTypeName(i18nLocale, loadedState, components));
  return {
    query,
    startSetComponentIds,
    currentContext,
    locale,
    onSelectAll: () => setStartQueryForLoadedState(loadedState, query),
    onContextDropdownComponentSelected: (componentIds: string[]) =>
      setStartSetForLoadedState(loadedState, componentIds),
    tracking: {
      onDropdownOpened: () =>
        trackEventV2({
          event: 'Viewpoint Toolbar0 Opened context selector',
        }),
      onComponentSelected: (numberOfComponents: number) =>
        trackEventV2({
          event: 'Viewpoint Toolbar0 Component selected in context selector',
          payload: { numberOfComponents },
        }),
      onSelectAllClicked: () =>
        trackEventV2({
          event: 'Viewpoint Toolbar0 Select all clicked in context selector',
        }),
    },
  };
};

const getOpenInInventoryButton = (
  i18nLocale: I18nLocale,
  loadedStateParams: LoadedStateParams[]
): ViewpointToolBarButton => ({
  onClick: () =>
    dispatchAction(
      navigate(
        toInventoryLocation({
          inventoryModule: InventoryModule.BROWSE,
          dataSourceType: 'dataset',
          useCase: 'pageManageData',
          loadedStateParams,
        })
      )
    ),
  text: t(
    i18nLocale,
    'canvas:contextToolbarOpenInInventory',
    'Open in Inventory'
  ),
  isActive: false,
  iconName: IconName.INVENTORY,
  buttonSize: ButtonSize.SMALL,
  isDisabled: loadedStateParams.length === 0,
});

const getDatasetsButton = (
  i18nLocale: I18nLocale,
  loadedState: LoadedState[],
  currentOpenSidebar: SidebarPanelContentIds | null
): ViewpointToolBarButton => ({
  onClick: () => toggleSidebar(SidebarPanelContentIds.DATASETS),
  text:
    loadedState.length > 1
      ? t(
          i18nLocale,
          'canvas:contextToolbarDatasetMultiple',
          { count: loadedState.length },
          `${loadedState.length} datasets`
        )
      : t(i18nLocale, 'canvas:contextToolbarDatasetSingle', '1 dataset'),
  isActive: currentOpenSidebar === SidebarPanelContentIds.DATASETS,
  iconName: IconName.DATASET,
  buttonSize: ButtonSize.SMALL,
  // Currently no scenario where this should be disabled
  isDisabled: false,
});

const getTypeName = (
  i18nLocale: I18nLocale,
  loadedState: LoadedState,
  components: StreamCollection<APIComponentAttributes>
): string => {
  const componentType = getStartTypeFromLoadedState(loadedState, components);
  if (!componentType) {
    return t(i18nLocale, 'canvas:contextToolbarUnknownType', 'Unknown type');
  }
  return componentType;
};

const getRepresentationData = (
  currentContext: string[],
  isMultipleTypesSelected: boolean,
  componentRepresentationDataByComponentId: Map<
    ArdoqId,
    ComponentRepresentationData | null
  >,
  components: StreamCollection<APIComponentAttributes>,
  workspaceAndModel: WorkspaceAndModelLookup
) => {
  if (currentContext.length === 1) {
    const component = components.byId[currentContext[0]];
    if (!isComponent(component)) return null;
    return getRepresentationDataWithColorVariantsForComponent(
      component,
      scopeDataOperations.getComponentTypeFromWorkspaceLookup(
        workspaceAndModel,
        component
      )
    );
  }
  if (isMultipleTypesSelected) {
    return null;
  }

  const representationData =
    componentRepresentationDataByComponentId.get(currentContext[0]) ?? null;
  return representationData
    ? getRepresentationDataWithColorVariants(representationData)
    : null;
};

const getDisabledButton = (
  i18nLocale: I18nLocale,
  buttonId: ToolbarButtonId,
  disabledReason: ViewpointToolBarDisabledReason
): Omit<ViewpointToolBarButton, 'text' | 'iconName'> => {
  return {
    onClick: () => undefined,
    isActive: false,
    buttonSize: ButtonSize.SMALL,
    isDisabled: true,
    disabledPopoverText: getDisabledButtonPopoverText(
      i18nLocale,
      buttonId,
      disabledReason
    ),
  };
};

const maybeGetDisabledReason = (
  visibleLoadedState: LoadedState[],
  graphSelection: string[],
  isMultipleTypesSelected: boolean
): ViewpointToolBarDisabledReason | null => {
  if (visibleLoadedState.length === 0)
    return ViewpointToolBarDisabledReason.NO_VISIBLE_DATASETS;
  if (visibleLoadedState.length !== 1 && graphSelection.length === 0)
    return ViewpointToolBarDisabledReason.MULTIPLE_DATASETS;
  if (isMultipleTypesSelected)
    return ViewpointToolBarDisabledReason.MULTIPLE_TYPES_SELECTED;
  return null;
};

export const getStartSetIds = (loadedState: LoadedState) => {
  if (!loadedState) return [];
  if (
    (loadedState.componentIdsAndReferences?.startSetResult?.length ?? 0) > 0
  ) {
    return loadedState.componentIdsAndReferences.startSetResult;
  }
  if (
    isTraversalLoadedState(loadedState) &&
    hasStartSet(loadedState.data.componentSelection)
  ) {
    return loadedState.data.componentSelection.startSet;
  }
  return [];
};

type ToViewpointToolBarPropsArgs = {
  i18nLocale: I18nLocale;
  locale: Locale;
  graphSelection: string[];
  loadedState: LoadedStatesState;
  sidebarPanel: SidebarPanelState;
  viewpoints: CollectionStream<APIViewpointAttributes>;
  isDiscover: boolean;
  components: StreamCollection<APIComponentAttributes>;
  componentRepresentationDataByComponentId: Map<
    ArdoqId,
    ComponentRepresentationData | null
  >;
  workspaceAndModel: WorkspaceAndModelLookup;
  featureContext: FeatureContext;
  priorityViewpointIds: ArdoqId[];
};

type GetPriorityViewpointItemsArgs = {
  viewpoints: CollectionStream<APIViewpointAttributes>;
  startType: string;
  priorityViewpointIds: ArdoqId[];
  currentViewpointId: string | null;
  componentSelection: ComponentSelection;
  defaultOverviewDescription: string;
  isWildcardTraversal: boolean;
  i18nLocale: I18nLocale;
  isFirstOpenedTraversal: boolean;
};

export const getPriorityViewpointItems = ({
  viewpoints,
  startType,
  priorityViewpointIds,
  currentViewpointId,
  componentSelection,
  defaultOverviewDescription,
  isWildcardTraversal,
  i18nLocale,
  isFirstOpenedTraversal,
}: GetPriorityViewpointItemsArgs): PriorityViewpointItem[] => {
  const prioritySet = new Set(priorityViewpointIds);
  const orderMap = new Map(
    priorityViewpointIds.map((id, index) => [id, index])
  );
  return [
    ...viewpoints.list.filter(viewpoint => viewpoint.startType === startType),
    getDefaultViewpoint({
      startType,
      description: defaultOverviewDescription,
      i18nLocale,
    }),
  ]
    .filter(viewpoint => prioritySet.has(viewpoint._id))
    .sort((a, b) => {
      const aIndex = orderMap.get(a._id) ?? Infinity;
      const bIndex = orderMap.get(b._id) ?? Infinity;
      return aIndex - bIndex;
    })
    .map(viewpoint => ({
      name: viewpoint.name,
      viewName: viewpoint.viewName,
      description: viewpoint.description ?? undefined,
      isCurrent: isCurrentViewpointId({
        viewpointId: viewpoint._id,
        currentViewpointId,
        isWildcardTraversal,
      }),
      isDefaultOverview: isDefaultOverviewId(viewpoint._id),
      onSelect: () =>
        viewpointSwitcherCommands.setNewViewpoint({
          viewpoint,
          componentSelection,
          source: 'toolbar-dropdown',
          isFirstOpenedTraversal,
        }),
    }));
};

export const toViewpointToolBarProps = ({
  i18nLocale,
  locale,
  graphSelection,
  loadedState: { loadedStates },
  sidebarPanel,
  viewpoints,
  isDiscover,
  components,
  componentRepresentationDataByComponentId,
  workspaceAndModel,
  featureContext,
  priorityViewpointIds,
}: ToViewpointToolBarPropsArgs): ViewpointToolBarState => {
  const visibleLoadedStates = loadedStates.filter(state => !state.isHidden);

  /* Computed before the disabled early return, because every branch of the toolbar renders it. */
  const excludedComponentCount = new Set(
    visibleLoadedStates.flatMap(loadedState =>
      isTraversalLoadedState(loadedState)
        ? getExcludedComponentIds(loadedState.data)
        : []
    )
  ).size;
  const mainLoadedState = visibleLoadedStates[0];
  const startContext = getStartSetIds(mainLoadedState);

  const componentGraphSelection = graphSelection.filter(id =>
    isComponentId(id, components)
  );

  const currentSelection = componentGraphSelection.length
    ? componentGraphSelection
    : startContext;
  const { isMultipleTypesSelected, label } = getSelectionStatus(
    i18nLocale,
    currentSelection,
    components.byId[currentSelection[0]]?.name ?? null,
    components,
    workspaceAndModel
  );

  const datasetsButton = getDatasetsButton(
    i18nLocale,
    loadedStates,
    sidebarPanel.contentId
  );
  const isInventoryAlphaEnabled = featureOperations.hasFeature(
    featureContext,
    Features.INVENTORY_ALPHA
  );
  const openInInventoryButton = isInventoryAlphaEnabled
    ? getOpenInInventoryButton(
        i18nLocale,
        visibleLoadedStates.map(
          loadedStateOperations.getLoadedStateParamsFromLoadedState
        )
      )
    : undefined;
  const disabledReason = maybeGetDisabledReason(
    visibleLoadedStates,
    componentGraphSelection,
    isMultipleTypesSelected
  );

  if (disabledReason) {
    return {
      status: 'disabled',
      excludedComponentCount,
      datasetCount: loadedStates.length,
      disabledReason,
      isDiscover,
      datasetsButton,
      openInInventoryButton,
      viewpointButton: {
        ...getDisabledButton(i18nLocale, 'viewpoint', disabledReason),
        iconName: IconName.VIEWPOINT,
        text: t(
          i18nLocale,
          'canvas:contextToolbarViewpointsText',
          'Viewpoints'
        ),
      },
      contextDropdownDisabledText: getDisabledButtonPopoverText(
        i18nLocale,
        'context',
        disabledReason
      ),
      i18nLocale,
    };
  }

  const representationData = getRepresentationData(
    currentSelection,
    isMultipleTypesSelected,
    componentRepresentationDataByComponentId,
    components,
    workspaceAndModel
  );

  const isUserSelectionMatchingStartSet = isSameSet(
    startContext,
    componentGraphSelection
  );

  const currentContext: CurrentToolbarContext = {
    label,
    selectedIds: currentSelection,
    representationData,
    componentSelection: getComponentSelection(
      mainLoadedState,
      componentGraphSelection
    ),
    isGraphSelection: componentGraphSelection.length > 0,
    startType:
      componentGraphSelection.length > 0
        ? getStartTypeFromIds(
            componentGraphSelection,
            components,
            workspaceAndModel
          )
        : getStartTypeFromLoadedState(mainLoadedState, components),
  };
  const currentViewpointId =
    mainLoadedState && isTraversalLoadedState(mainLoadedState)
      ? (mainLoadedState.traversalId ?? null)
      : null;

  const isWildcardTraversal = getIsWildcardTraversal(mainLoadedState);

  const viewpointButton = getViewpointButton({
    i18nLocale,
    currentViewpointId,
    viewpoints: viewpoints.byId,
    startType: currentContext.startType,
    currentSidebar: sidebarPanel.contentId,
    isWildcardTraversal,
    isMultipleTypesSelected,
    graphSelection: componentGraphSelection,
    isUserSelectionMatchingStartSet,
  });
  const detailsButton = getDetailButton(
    i18nLocale,
    sidebarPanel.contentId,
    loadedStates.length,
    currentSelection.filter(id => isComponentId(id, components)),
    isDiscover
  );
  const priorityViewpointItems = currentContext.startType
    ? getPriorityViewpointItems({
        viewpoints,
        startType: currentContext.startType,
        priorityViewpointIds,
        currentViewpointId,
        componentSelection: currentContext.componentSelection,
        isFirstOpenedTraversal: loadedStates.length === 0,
        isWildcardTraversal,
        i18nLocale,
        defaultOverviewDescription: t(
          i18nLocale,
          'canvas:viewpointDropdownDefaultOverviewDescription',
          { componentType: currentContext.startType },
          `Shows everything directly connected to this ${currentContext.startType}`
        ),
      })
    : [];

  return {
    status: 'enabled',
    currentContext,
    currentViewpointId,
    currentOpenSidebar: sidebarPanel.contentId,
    datasetCount: loadedStates.length,
    viewpointButton,
    priorityViewpointItems,
    seeAllViewpointsLabel: t(
      i18nLocale,
      'canvas:viewpointDropdownSeeAll',
      { componentType: currentContext.startType },
      `See all ${currentContext.startType} Viewpoints`
    ),
    defaultOverviewLabel: t(
      i18nLocale,
      'canvas:defaultOverviewBadge',
      'Ardoq default'
    ),
    detailsButton,
    contextDropdown: getContextDropdown(
      i18nLocale,
      locale,
      mainLoadedState,
      currentContext,
      components
    ),
    datasetsButton,
    openInInventoryButton,
    isDiscover,
    isWildcardTraversal,
    clearSelection: () => dispatchAction(clearGraphSelection()),
    excludedComponentCount,
    i18nLocale,
  };
};
